import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { BrandMark } from "./Brand";
import { Button, Spinner } from "./Button";
import { cx } from "./format";


/** Grey placeholder block that shimmers while data loads. */
export const Skeleton = ({ className, style }) => (
  <div
    aria-hidden="true"
    className={cx(
      "rounded-md bg-[linear-gradient(90deg,var(--cm-surface-2),var(--cm-surface-3),var(--cm-surface-2))] bg-[length:200%_100%] animate-cm-shimmer",
      className,
    )}
    style={style}
  />
);

/** A few skeleton rows shaped like a table. */
export const TableSkeleton = ({ rows = 6, cols = 5 }) => (
  <div className="flex flex-col gap-3 p-4" aria-label="Loading" role="status">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {Array.from({ length: cols }).map((__, c) => (
          <Skeleton key={c} className="h-4" style={{ width: `${55 + ((r * 7 + c * 13) % 40)}%` }} />
        ))}
      </div>
    ))}
  </div>
);

/** Full-area loader with the ChipMonk mark. */
export const PageLoader = ({ label = "Loading…", className, fullScreen = false }) => (
  <div
    role="status"
    className={cx(
      "flex flex-col items-center justify-center gap-4 text-cm-muted",
      fullScreen ? "min-h-screen bg-cm-bg" : "min-h-[40vh]",
      className,
    )}
  >
    <div className="relative">
      <BrandMark size={56} className="opacity-90" />
      <span className="absolute -bottom-1 -right-1 bg-cm-bg rounded-full p-0.5 text-cm-gold">
        <Spinner size={16} />
      </span>
    </div>
    <p className="text-sm m-0">{label}</p>
  </div>
);

/** Empty never means blank: say what will appear here and how to fill it. */
export const EmptyState = ({ title = "Nothing here yet", description, action, icon: Icon, compact = false, className }) => (
  <div className={cx("flex flex-col items-center text-center gap-2", compact ? "py-8 px-4" : "py-12 px-4", className)}>
    {Icon ? (
      <span className="w-12 h-12 rounded-cm-lg grid place-items-center bg-cm-gold-soft text-cm-gold-text mb-1">
        <Icon size={22} />
      </span>
    ) : (
      <BrandMark size={compact ? 56 : 76} className="mb-1 opacity-95" />
    )}
    <h3 className="text-base font-bold text-cm-text m-0">{title}</h3>
    {description && <p className="text-sm text-cm-muted max-w-sm m-0">{description}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

/** What went wrong and how to fix it. `onRetry` shows a Try again button. */
export const ErrorState = ({ title = "Couldn't load this", description, onRetry, className }) => (
  <div className={cx("flex flex-col items-center text-center gap-2 py-10 px-4", className)} role="alert">
    <span className="w-12 h-12 rounded-cm-lg grid place-items-center bg-cm-danger-soft text-cm-danger mb-1">
      <AlertTriangle size={22} />
    </span>
    <h3 className="text-base font-bold text-cm-text m-0">{title}</h3>
    {description && <p className="text-sm text-cm-muted max-w-sm m-0">{description}</p>}
    {onRetry && (
      <Button variant="secondary" size="sm" icon={RefreshCw} onClick={onRetry} className="mt-2">
        Try again
      </Button>
    )}
  </div>
);

/** Inline notice banner (info / warning / danger / success). */
export const Notice = ({ tone = "info", title, children, icon: Icon, action, className }) => {
  const cls = {
    info: "bg-cm-info-soft text-cm-info",
    warning: "bg-cm-warning-soft text-cm-warning",
    danger: "bg-cm-danger-soft text-cm-danger",
    success: "bg-cm-success-soft text-cm-success",
    gold: "bg-cm-gold-soft text-cm-gold-text",
  }[tone];
  return (
    <div className={cx("flex items-start gap-3 rounded-cm-lg px-4 py-3", cls, className)} role={tone === "danger" ? "alert" : "status"}>
      {Icon && <Icon size={18} className="shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0 text-sm">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className="text-cm-text">{children}</div>}
      </div>
      {action}
    </div>
  );
};
